import type React from "react";
import { useParams } from "@tanstack/react-router";
import { useFormContext } from "react-hook-form";
import { ui } from "@patient-watch/ui";
import { cn } from "@patient-watch/ui";
import type { InputT } from "@patient-watch/types";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import { parseMsAdlRatingsScaleOptions } from "@/features/entry-values/utils/msAdlRatingsListHelpers";
import { useMsAdlCopy } from "@/features/i18n/msAdlCopy";
import { services as s } from "@/services";

interface MsAdlSelectionProps {
    input: InputT;
    name: string;
    label?: string;
    description?: string;
}

const MsAdlSelection: React.FC<MsAdlSelectionProps> = ({ input, name, label, description }) => {
    const { entryId } = useParams({ strict: false });
    const { watch, setValue } = useFormContext();
    const { formatActivity } = useMsAdlCopy();
    const upsertEntryValue = useEntryValuesStore((state) => state.upsertEntryValue);
    const existingEntryValue = useEntryValuesStore((state) => state.getEntryValueByInputId(name));
    const scale = parseMsAdlRatingsScaleOptions(input.scale_options);
    const activityOptions = input.options ?? [];
    const selectedActivities = (watch(name) as string[]) || [];

    const handleToggleActivity = (activity: string, selected: boolean) => {
        const current = new Set(selectedActivities);
        if (selected) {
            if (current.size >= scale.maxActivities) return;
            current.add(activity);
        } else {
            current.delete(activity);
        }
        const nextActivities = Array.from(current);
        setValue(name, nextActivities);

        if (entryId) {
            upsertEntryValue({
                entry_id: entryId,
                input_id: name,
                entry_value_id: existingEntryValue?.entry_value_id ?? s.uuidv4(),
                text_array: nextActivities,
                number_value: null,
                text_value: null,
                json_value: null,
                object_id: null,
            });
        }
    };

    return (
        <section className="pt-4">
            <div className="space-y-4">
                <div>
                    {label && (
                        <ui.Label className="font-semibold text-base">
                            {label}
                            {!input.is_optional && <span className="ml-0.5 text-red-500">*</span>}
                        </ui.Label>
                    )}
                    {description && <p className="mt-1 text-muted-foreground text-sm">{description}</p>}
                    <p className="mt-2 text-muted-foreground text-sm">
                        Select {scale.minActivities} to {scale.maxActivities} activities of daily living that are
                        important to you.
                    </p>
                </div>

                <ul className="flex flex-col gap-y-2">
                    {activityOptions.map((activity) => {
                        const checked = selectedActivities.includes(activity);
                        const atMax = selectedActivities.length >= scale.maxActivities;
                        const disabled = !checked && atMax;
                        const optionId = `${input.input_id}-${activity}`;

                        return (
                            <li key={activity}>
                                <div
                                    className={cn(
                                        "flex items-center gap-2 py-1 pl-2 rounded-md transition-[box-shadow] duration-200",
                                        checked && "ring-2 ring-indigo-600 dark:ring-indigo-400",
                                        disabled && "opacity-50 cursor-not-allowed"
                                    )}
                                >
                                    <ui.Checkbox
                                        id={optionId}
                                        checked={checked}
                                        disabled={disabled}
                                        onCheckedChange={(isChecked) =>
                                            handleToggleActivity(activity, isChecked === true)
                                        }
                                    />
                                    <label
                                        htmlFor={optionId}
                                        className={cn(
                                            "py-2.5 w-full font-normal text-sm leading-snug",
                                            disabled ? "cursor-not-allowed" : "cursor-pointer"
                                        )}
                                    >
                                        {formatActivity(activity)}
                                    </label>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
};

export default MsAdlSelection;
